import { Modal, Form, Button } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TimeEntryDto } from '@/types';
import { DurationInputGroup, DurationField } from './TimeEntryModal.styled';

interface TimeEntryModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (data: {
    description: string;
    durationMinutes: number;
    date: string;
    notes?: string;
  }) => void;
  entry?: TimeEntryDto | null;
  isLoading?: boolean;
}

interface FormValues {
  description: string;
  hours: number;
  minutes: number;
  date: string;
  notes: string;
}

export default function TimeEntryModal({
  show, onHide, onSubmit, entry, isLoading,
}: TimeEntryModalProps) {
  const { t } = useTranslation();

  const { register, handleSubmit, reset, watch, formState: { isValid } } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      description: '',
      hours: 0,
      minutes: 0,
      date: '',
      notes: '',
    },
  });

  const hours = watch('hours');
  const minutes = watch('minutes');

  const handleEnter = () => {
    if (entry) {
      reset({
        description: entry.description,
        hours: Math.floor(entry.durationMinutes / 60),
        minutes: entry.durationMinutes % 60,
        date: entry.date.slice(0, 10),
        notes: entry.notes ?? '',
      });
    } else {
      reset({
        description: '',
        hours: 0,
        minutes: 0,
        date: new Date().toISOString().slice(0, 10),
        notes: '',
      });
    }
  };

  const submit = handleSubmit((values) => {
    onSubmit({
      description: values.description,
      durationMinutes: Number(values.hours) * 60 + Number(values.minutes),
      date: new Date(values.date).toISOString(),
      notes: values.notes || undefined,
    });
  });

  const hasDuration = Number(hours) > 0 || Number(minutes) > 0;

  return (
    <Modal show={show} onHide={onHide} onEnter={handleEnter} centered>
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {entry ? t('timeEntries.editEntry') : t('timeEntries.addEntry')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t('timeEntries.description')}</Form.Label>
            <Form.Control
              type="text"
              placeholder={t('timeEntries.descriptionPlaceholder')}
              maxLength={500}
              {...register('description', { required: true, validate: (v) => v.trim().length > 0 })}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('timeEntries.duration')}</Form.Label>
            <DurationInputGroup>
              <DurationField>
                <Form.Control
                  type="number"
                  min={0}
                  max={23}
                  {...register('hours', { valueAsNumber: true, min: 0, max: 23 })}
                />
                <Form.Text muted>{t('timeEntries.hours')}</Form.Text>
              </DurationField>
              <DurationField>
                <Form.Control
                  type="number"
                  min={0}
                  max={59}
                  {...register('minutes', { valueAsNumber: true, min: 0, max: 59 })}
                />
                <Form.Text muted>{t('timeEntries.minutes')}</Form.Text>
              </DurationField>
            </DurationInputGroup>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('timeEntries.date')}</Form.Label>
            <Form.Control type="date" {...register('date', { required: true })} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('timeEntries.notes')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={2000}
              {...register('notes')}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={!isValid || !hasDuration || isLoading}>
            {entry ? t('common.save') : t('timeEntries.addEntry')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
